import { Avatar, AvatarFallback } from '@/components/shadcn/avatar'
import { Button } from '@/components/shadcn/button'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { RestSchemas } from '@/contexts/QueryProvider'
import { cn, getInitials } from '@/lib/utils'
import { MaintainProjectMembersForm } from '@/pages/Projects/Forms/MaintainProjectMembersForm'
import { MaintainProjectAdminsForm } from '@/pages/Subscriptions/Forms/MaintainProjectAdminsForm'
import { MaintainProjectLeadersForm } from '@/pages/Subscriptions/Forms/MaintainProjectLeadersForm'
import { IconCalendarOutline } from '@icons/IconCalendarOutline'
import { IconGlobeOutline } from '@icons/IconGlobeOutline'
import { IconShieldOutline } from '@icons/IconShieldOutline'
import { IconV2EditOutline } from '@icons/IconV2EditOutline'
import clsx from 'clsx'
import { useMemo } from 'react'
// import { Link } from 'react-router'
import { ProjectLinkedWorkspaceGridItem, ProjectNewWorkspaceGridItem } from './ProjectWorkspacesGridItem'
import { ProjectWorkspace } from './projectWorkspaceUtils'
import { ProjectQuickLinks } from './ProjectQuickLinks'

type ProjectUser = RestSchemas['Messages.Projects.OrganisationProjectUser']
type OrganisationProject = RestSchemas['Messages.Projects.OrganisationProject']

function ProjectUserAvatars({ users }: { users: ProjectUser[] }) {
  if (users.length === 0) {
    return <span className="text-sm text-muted-foreground">None</span>
  }

  return (
    <div className="flex shrink-0 -space-x-2">
      {users.map((user, index) => (
        <TooltipButton key={user.userId} tooltip={user.name}>
          <span className="inline-flex">
            <Avatar className="ring-2 ring-background">
              <AvatarFallback
                className={clsx(
                  'bg-accent text-accent-foreground',
                  index % 2 !== 0 && 'bg-secondary text-secondary-foreground'
                )}
              >
                {getInitials(user.name)}
              </AvatarFallback>
            </Avatar>
          </span>
        </TooltipButton>
      ))}
    </div>
  )
}

function ProjectDetailRow({
  icon: Icon,
  label,
  children,
  action,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  children: React.ReactNode
  action?: React.ReactNode
}) {
  return (
    <li className="flex items-center gap-3 py-3">
      <Icon className="size-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1 text-sm text-foreground">{label}</span>
      <div className="flex shrink-0 items-center gap-2">
        {children}
        {action}
      </div>
    </li>
  )
}

export function ProjectLinkedWorkspacesPanel({
  children,
  scrollable = false,
}: {
  children: React.ReactNode
  scrollable?: boolean
}) {
  return (
    <div className="flex min-h-0 min-w-0 flex-col gap-3 border-border md:border-l md:pl-6">
      <h4 className="shrink-0 text-base font-bold text-foreground">Linked workspaces</h4>
      {scrollable ? <ScrollArea className="min-h-0 flex-1">{children}</ScrollArea> : children}
    </div>
  )
}

export function ProjectWorkspacesSection({
  workspaces,
  projectId,
  organisationId,
  projectName,
  canCreateWorkspace = false,
  canRequestWorkspace = false,
  excludedAppIds = [],
  scrollWorkspaces,
  className,
}: {
  workspaces: ProjectWorkspace[]
  projectId: number
  organisationId: number
  projectName?: string
  canCreateWorkspace?: boolean
  canRequestWorkspace?: boolean
  excludedAppIds?: number[]
  scrollWorkspaces?: boolean
  className?: string
}) {
  const sortedWorkspaces = useMemo(
    () => [...workspaces].sort((a, b) => a.application.localeCompare(b.application)),
    [workspaces]
  )

  const canAddWorkspace = organisationId > 0 && projectName != null && canCreateWorkspace
  const canRequest = organisationId > 0 && projectName != null && canRequestWorkspace

  if (sortedWorkspaces.length === 0 && !canAddWorkspace && !canRequest) {
    return <p className="text-sm text-muted-foreground">No linked workspaces.</p>
  }

  const grid = (
    <div className={cn('grid grid-cols-[repeat(auto-fill,minmax(9rem,1fr))] pr-3', className)}>
      {sortedWorkspaces.map((workspace) => (
        <ProjectLinkedWorkspaceGridItem
          key={workspace.workspaceId}
          workspace={workspace}
          projectId={projectId}
          organisationId={organisationId}
        />
      ))}
      {(canAddWorkspace || canRequest) && (
        <ProjectNewWorkspaceGridItem
          organisationId={organisationId}
          projectId={projectId}
          projectName={projectName}
          excludedAppIds={excludedAppIds}
          canAddWorkspace={canAddWorkspace}
          canRequestWorkspace={canRequest}
        />
      )}
    </div>
  )

  if (scrollWorkspaces) {
    return <ScrollArea className="max-h-[336px]">{grid}</ScrollArea>
  }

  return grid
}

function ProjectDetailsContent({
  leaders,
  administrators,
  members,
  frequency,
  organisationId,
  selectedProject,
  canEditProjectLeaders = false,
  canEditProjectAdmins = false,
  canEditMembers = false,
}: {
  leaders: ProjectUser[]
  administrators: ProjectUser[]
  members: ProjectUser[]
  frequency: string | null | undefined
  organisationId?: number
  selectedProject?: OrganisationProject
  canEditProjectLeaders?: boolean
  canEditProjectAdmins?: boolean
  canEditMembers?: boolean
}) {
  const hasProjectDetails = leaders.length > 0 || administrators.length > 0 || members.length > 0 || !!frequency
  const canMaintainLeaders = organisationId != null && selectedProject != null && canEditProjectLeaders
  const canMaintainAdmins = selectedProject != null && canEditProjectAdmins
  const canMaintainMembers = organisationId != null && selectedProject != null && canEditMembers

  if (!hasProjectDetails && !canMaintainLeaders && !canMaintainAdmins && !canMaintainMembers) {
    return <p className="text-sm text-muted-foreground">No project details to display.</p>
  }

  return (
    <ul className="flex flex-col divide-y divide-border">
      <ProjectDetailRow
        icon={IconCalendarOutline}
        label="Project leaders"
        action={
          canMaintainLeaders ? (
            <MaintainProjectLeadersForm
              organisationId={organisationId}
              selectedProject={selectedProject}
              sheetTitle={`${selectedProject.name} > Maintain leaders`}
              sheetDescription="Edit the leaders of the project."
              sheetTriggerTooltip="Maintain project leaders"
              sheetTrigger={
                <Button variant="ghost" size="icon" type="button">
                  <IconV2EditOutline className="size-5" />
                  <span className="sr-only">Maintain project leaders</span>
                </Button>
              }
            />
          ) : undefined
        }
      >
        <ProjectUserAvatars users={leaders} />
      </ProjectDetailRow>
      <ProjectDetailRow
        icon={IconGlobeOutline}
        label="Project administrators"
        action={
          canMaintainAdmins ? (
            <MaintainProjectAdminsForm
              selectOrganisation
              selectedProject={selectedProject}
              sheetTitle={`${selectedProject.name} > Maintain admins`}
              sheetDescription="Edit the admins of the project."
              sheetTriggerTooltip="Maintain project admins"
              sheetTrigger={
                <Button variant="ghost" size="icon" type="button">
                  <IconV2EditOutline className="size-5" />
                  <span className="sr-only">Maintain project admins</span>
                </Button>
              }
            />
          ) : undefined
        }
      >
        <ProjectUserAvatars users={administrators} />
      </ProjectDetailRow>
      <ProjectDetailRow
        icon={IconShieldOutline}
        label="Project members"
        action={
          canMaintainMembers ? (
            <MaintainProjectMembersForm
              organisationId={organisationId}
              selectedProject={selectedProject}
              members={members}
              sheetTitle={`${selectedProject.name} > Maintain members`}
              sheetDescription="Add members from an organisation you have access to. Members can access Databricks notebooks and catalogue workspaces linked to this project."
              sheetTriggerTooltip="Maintain project members"
              sheetTrigger={
                <Button variant="ghost" size="icon" type="button">
                  <IconV2EditOutline className="size-5" />
                  <span className="sr-only">Maintain project members</span>
                </Button>
              }
            />
          ) : undefined
        }
      >
        <ProjectUserAvatars users={members} />
      </ProjectDetailRow>
      {frequency && (
        <ProjectDetailRow icon={IconCalendarOutline} label="Frequency">
          <span className="text-sm text-foreground">{frequency}</span>
        </ProjectDetailRow>
      )}
    </ul>
  )
}

export function ProjectDetailsLayout({
  projectId,
  leaders,
  administrators,
  members,
  frequency,
  organisationId,
  selectedProject,
  canEditProjectLeaders = false,
  canEditProjectAdmins = false,
  canEditMembers = false,
  showProjectQuickLinks = false,
  showDmpChat = false,
  scrollDetails = false,
  className,
  children,
}: {
  projectId?: number
  leaders: ProjectUser[]
  administrators: ProjectUser[]
  members: ProjectUser[]
  frequency: string | null | undefined
  organisationId?: number
  selectedProject?: OrganisationProject
  canEditProjectLeaders?: boolean
  canEditProjectAdmins?: boolean
  canEditMembers?: boolean
  showProjectQuickLinks?: boolean
  showDmpChat?: boolean
  scrollDetails?: boolean
  className?: string
  children: React.ReactNode
}) {
  const detailsContent = (
    <ProjectDetailsContent
      leaders={leaders}
      administrators={administrators}
      members={members}
      frequency={frequency}
      organisationId={organisationId}
      selectedProject={selectedProject}
      canEditProjectLeaders={canEditProjectLeaders}
      canEditProjectAdmins={canEditProjectAdmins}
      canEditMembers={canEditMembers}
    />
  )

  const quickLinks =
    showProjectQuickLinks && projectId != null ? (
      <ProjectQuickLinks projectId={projectId} showDmpChat={showDmpChat} className="border-t border-border" />
    ) : null

  return (
    <div
      className={cn(
        'grid gap-6 bg-card px-5 pb-5 pt-4 md:grid-cols-[1fr_2fr]',
        scrollDetails && 'h-full min-h-0 flex-1',
        className
      )}
    >
      <div className="flex min-h-0 min-w-0 flex-col gap-3">
        <h4 className="shrink-0 text-base font-bold text-foreground">Project details</h4>
        {scrollDetails ? (
          <ScrollArea className="min-h-0 flex-1">
            {detailsContent}
            {quickLinks}
          </ScrollArea>
        ) : (
          <>
            {detailsContent}
            {quickLinks}
          </>
        )}
      </div>

      {children}
    </div>
  )
}
